/**
 * Daily calorie intake calculations. Kcal-only, and import-free so the
 * co-located test resolves under bare `node --test`.
 *
 * The BMR equation and the `* 1.2 - deficit` maintenance adjustment are carried
 * over verbatim from the legacy cppv3 flow; see
 * docs/architecture/daily-kcal.md for what changed around them.
 */

/** Sedentary activity multiplier applied to BMR to reach maintenance kcal. */
export const DAILY_KCAL_ACTIVITY_FACTOR = 1.2

/** Deficit subtracted from maintenance. Fixed — cppv3's "optimal intake" value. */
export const DAILY_KCAL_DEFICIT = 300

/** Rounding step for the published amount. Fixed — the spec pins it at the nearest 100. */
export const DAILY_KCAL_STEP = 100

/** Half-width of the published range. Fixed — the band is contractually 200 kcal wide. */
export const DAILY_KCAL_RANGE_HALF_WIDTH = 100

/** `dailyKcalMax - dailyKcalMin`, always. */
export const DAILY_KCAL_RANGE_WIDTH = DAILY_KCAL_RANGE_HALF_WIDTH * 2

/** Kcal the complete meals cover, subtracted from the range for the leftovers. */
export const DEFAULT_COMPLETEMEALS_COVER = 800

export interface BmrInput {
  gender?: string | null
  age: number
  heightCm: number
  weightKg: number
}

export interface DailyKcalRange {
  dailyKcal: number
  dailyKcalMin: number
  dailyKcalMax: number
}

export interface DailyKcalLeftover {
  dailyKcalLeftoverMin: number
  dailyKcalLeftoverMax: number
}

/** Normalize an optional override to a finite number `>= 0`, else the fallback. */
function nonNegativeOr(raw: number | undefined, fallback: number): number {
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

/**
 * Basal metabolic rate, original Harris-Benedict.
 *
 * @param input gender, age in years, height in cm, weight in kg
 * @returns kcal/day, or `null` when age, height or weight is not a positive
 *   finite number. Any gender other than `male` uses the female equation,
 *   which is the legacy fallback for an unanswered gender step.
 */
export function getBmr({ gender, age, heightCm, weightKg }: BmrInput): number | null {
  const weight = Number(weightKg)
  const height = Number(heightCm)
  const years = Number(age)
  if (!Number.isFinite(weight) || weight <= 0) return null
  if (!Number.isFinite(height) || height <= 0) return null
  if (!Number.isFinite(years) || years <= 0) return null

  if (typeof gender === 'string' && gender.toLowerCase() === 'male') {
    return 66.47 + 13.75 * weight + 5 * height - 6.76 * years
  }

  return 655.1 + 9.56 * weight + 1.85 * height - 4.68 * years
}

/**
 * Recommended daily intake as one amount plus a fixed-width range.
 *
 * The amount is floored at `DAILY_KCAL_RANGE_HALF_WIDTH` so `dailyKcalMin` is
 * never negative without narrowing the band — `dailyKcalMax - dailyKcalMin` is
 * always `DAILY_KCAL_RANGE_WIDTH`.
 *
 * @param input gender, age in years, height in cm, weight in kg
 * @returns the amount and its bounds, or `null` when the BMR inputs are
 *   incomplete
 */
export function getDailyKcalRange(input: BmrInput): DailyKcalRange | null {
  const bmr = getBmr(input)
  if (bmr === null) return null

  const maintenance = bmr * DAILY_KCAL_ACTIVITY_FACTOR - DAILY_KCAL_DEFICIT
  const dailyKcal = Math.max(
    DAILY_KCAL_RANGE_HALF_WIDTH,
    Math.round(maintenance / DAILY_KCAL_STEP) * DAILY_KCAL_STEP,
  )

  return {
    dailyKcal,
    dailyKcalMin: dailyKcal - DAILY_KCAL_RANGE_HALF_WIDTH,
    dailyKcalMax: dailyKcal + DAILY_KCAL_RANGE_HALF_WIDTH,
  }
}

/**
 * The part of the intake range the complete meals do not cover.
 *
 * @param range the published amount range
 * @param coverKcal kcal the complete meals cover; defaults to
 *   `DEFAULT_COMPLETEMEALS_COVER`
 * @returns both bounds clamped at 0, so a cover above the range reads as 0
 *   rather than a negative leftover
 */
export function getDailyKcalLeftover(range: DailyKcalRange, coverKcal?: number): DailyKcalLeftover {
  const cover = nonNegativeOr(coverKcal, DEFAULT_COMPLETEMEALS_COVER)

  return {
    dailyKcalLeftoverMin: Math.max(0, range.dailyKcalMin - cover),
    dailyKcalLeftoverMax: Math.max(0, range.dailyKcalMax - cover),
  }
}
